import { defineStore } from 'pinia';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import {
  CreateRestaurantBookingRequestDTO,
  RestaurantDTO,
  BadRequestResponseDTO,
  RestaurantSetupDTO,
  RestaurantAvailabilityDTO,
  RestaurantPromotionDTO,
} from 'src/services/api/data-contracts';
import { Notify } from 'quasar';
import { AxiosError } from 'axios';
import { usePropertyStore } from './propertyStore';
import { i18n } from 'boot/i18n';

const t = i18n.global.t;

const apiService = Services.getService(Api);

export const useRestaurantTableStore = defineStore('restaurantTableStore', {
  state: () => ({
    active: false as boolean,
    serviceType: 'None' as 'None' | 'ResDiary' | 'SevenRooms',
    defaultRestaurant: null as string,

    restaurants: [] as RestaurantDTO[],
    selectedRestaurant: null as RestaurantDTO | null,

    restaurantSetup: null as RestaurantSetupDTO,

    selectedTime: null as RestaurantAvailabilityDTO,
    selectedPromotion: null as RestaurantPromotionDTO,

    partySize: null as number | null,
    partySizes: [] as Array<number>,

    visitDate: null as string | null,
    specialRequests: null as string | null,
    identifier: null as number | null,
    email: null as string | null,
    contactNumber: null as string | null,

    currencySymbol: '' as string,
    showPartySizes: false as boolean,
    showAvailability: false as boolean,
    restaurantAvailability: [] as Array<RestaurantAvailabilityDTO>,

    showNoAvailabilityMessage: false as boolean,
    noAvailabilityText: null as string,

    loadingRestaurantData: false as boolean,
    loadingAvailabilityData: false as boolean,

    isCreatingBooking: false as boolean,

    minBookingDate: '' as string,
    maxBookingDate: '' as string,

    showBookingReference: false as boolean,
    bookingReference: null as string,
  }),
  getters: {
    canCreateBooking(): boolean {
      let isValid = false;
      isValid =
        this.contactNumber != null &&
        this.contactNumber != '' &&
        this.email != null &&
        this.contactNumber != '';

      isValid =
        isValid &&
        this.identifier != null &&
        this.selectedRestaurant != null &&
        this.partySize != null &&
        this.visitDate != null &&
        this.selectedTime != null;

      return isValid;
    },
    promotions(): RestaurantPromotionDTO[] | null {
      return this.selectedTime?.Promotions;
    },
    hasPromotions(): boolean {
      return this.selectedTime?.Promotions?.length > 0;
    },
  },
  actions: {
    resetState(): void {
      this.selectedTime = null;
      this.selectedPromotion = null;
      this.showNoAvailabilityMessage = false;
      this.noAvailabilityText = null;
      this.showAvailability = false;
      this.showPartySizes = false;
      this.specialRequests = null;
      this.identifier = null;
      this.email = null;
      this.contactNumber = null;
      this.partySize = null;
      this.partySizes = [];
      this.restaurantAvailability = [];

      this.showBookingReference = false;
      this.bookingReference = null;

      this.selectDefaultRestaurant();
    },

    selectDefaultRestaurant() {
      const restaurant = this.restaurants.filter(
        (y) =>
          y.Name.trim().toLowerCase() ==
          this.defaultRestaurant.trim().toLowerCase(),
      );

      if (restaurant != null && restaurant.length > 0) {
        this.selectedRestaurant = restaurant[0];
      }
    },

    getRestaurants(propertyId: number) {
      apiService
        .restaurantBookingGetRestaurants({ propertyId: propertyId })
        .then((response) => {
          this.restaurants = response.data;

          this.selectDefaultRestaurant();
        });
    },

    getRestaurantSetup() {
      const ps = usePropertyStore();

      const restaurantId = this.selectedRestaurant?.Id;
      const visitDate = this.visitDate;
      if (restaurantId != null) {
        this.loadingRestaurantData = true;

        const query = {
          propertyId: ps.propertyId,
          restaurantId: restaurantId,
          date: visitDate,
          isReservation: true,
          identifier: this.identifier,
        };

        const setupPromise =
          apiService.restaurantBookingGetRestaurantSetup(query);

        setupPromise.then((setupResponse) => {
          this.restaurantSetup = setupResponse.data;

          const previousPartySize = this.partySize;
          this.currencySymbol = setupResponse.data.CurrencySymbol;
          this.showAvailability = false;
          this.showPartySizes = false;

          this.partySize = 0;
          this.partySizes = [];

          for (
            let i = setupResponse.data.MinOnlinePartySize;
            i <= setupResponse.data.MaxOnlinePartySize;
            i++
          ) {
            this.partySizes.push(i);
          }

          this.showPartySizes = true;
          this.noAvailabilityText = setupResponse.data.NoAvailabilityText;

          if (
            previousPartySize >= setupResponse.data.MinOnlinePartySize &&
            previousPartySize <= setupResponse.data.MaxOnlinePartySize
          ) {
            this.partySize = previousPartySize;
          }

          if (this.partySize == 0 && this.partySizes.length > 0) {
            this.partySize = this.partySizes[0];
          }

          const matchingRestaurants = this.restaurants.filter(
            (x) => x.Id === this.selectedRestaurant.Id,
          );
          if (matchingRestaurants.length > 0) {
            this.selectedRestaurant = matchingRestaurants[0];
          }

          if (this.partySize !== 0) {
            this.getAvailability();
          }

          this.setMinAndMaxDate();
        });

        setupPromise.finally(() => {
          this.loadingRestaurantData = false;
        });
      }
    },

    getAvailability() {
      const ps = usePropertyStore();

      const restaurant = this.selectedRestaurant;
      const date = this.visitDate;
      const partySize = this.partySize;

      if (restaurant != null && date != null && date != '' && partySize > 0) {
        this.loadingAvailabilityData = true;

        const query = {
          propertyId: ps.propertyId,
          restaurantId: this.selectedRestaurant.Id,
          date: date,
          partySize: partySize,
          currencySymbol: this.currencySymbol,
        };

        const getAvailabilityPromise =
          apiService.restaurantBookingGetAvailability(query);

        getAvailabilityPromise.then((availabilityResponse) => {
          const result = availabilityResponse.data;
          this.restaurantAvailability = result;
          this.showAvailability = result.length > 0;
          this.showNoAvailabilityMessage = result.length === 0;
        });

        getAvailabilityPromise.finally(() => {
          this.loadingAvailabilityData = false;
        });
      }
    },
    setMinAndMaxDate() {},

    createBooking() {
      const nb = usePropertyStore();
      this.isCreatingBooking = true;

      const resBooking = {
        PropertyId: nb.propertyId,
        RestaurantId: this.selectedRestaurant.Id,
        RestaurantName: this.selectedRestaurant.Name,

        VisitTime: this.selectedTime != null ? this.selectedTime.Time : '',
        VisitDate: this.visitDate,
        PartySize: this.partySize,

        SpecialRequests: this.specialRequests,
        IsReservation: true,

        Identifier: this.identifier,

        PromotionId:
          this.selectedPromotion != null ? this.selectedPromotion.Id : null,
        Email: this.email,
        ContactNumber: this.contactNumber,
        IsLeaveTimeRequired:
          this.selectedTime != null
            ? this.selectedTime.IsLeaveTimeRequired
            : false,
      } as CreateRestaurantBookingRequestDTO;

      const createBookingPromise =
        apiService.restaurantBookingCreate(resBooking);

      createBookingPromise.then((createBookingResponse) => {
        if (createBookingResponse.data.Success) {
          this.showBookingReference = true;
          this.bookingReference = createBookingResponse.data.BookingReference;
          const message =
            createBookingResponse.data?.InfoMessage ??
            t('restaurantService.successfullyCreateBooking');

          Notify.create({
            color: 'positive',
            position: 'top',
            message: message,
          });
        } else {
          const message =
            createBookingResponse.data?.InfoMessage ??
            t('restaurantService.failedToCreateBooking');

          Notify.create({
            color: 'negative',
            position: 'top',
            message: message,
          });
        }

        this.isCreatingBooking = false;
      });

      createBookingPromise.catch(
        (createBookingAxiosError: AxiosError<BadRequestResponseDTO>) => {
          const message =
            createBookingAxiosError.response.data?.Message ??
            t('restaurantService.failedToCreateBooking');

          Notify.create({
            color: 'negative',
            position: 'bottom',
            message: message,
          });

          this.isCreatingBooking = false;
        },
      );

      return createBookingPromise;
    },
  },
});
